import React, { useContext, useState, useEffect, useRef } from 'react';
import { AuthContext } from "../../auth/providers/AuthProvider";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { addNewStatement, listTemplateStatements, fetchStatementsPage } from "../../../services/template/Statements";
import FormStatements from "../forms/FormStatements";
import { CsrfContext } from "../../../providers/CsrfProvider";
import { TemplateContext } from "../../../providers/TemplateProvider";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { ThemeContext } from "styled-components";
import { Alert, AlertTitle } from "@mui/material";

const StatementsModel = ({ templateId, enabled = true, hydrating = false, onHydrated, expandedView = false, headerAction }) => {
    const { csrfToken } = useContext(CsrfContext);
    const { ownerSub } = useContext(TemplateContext);
    const { authClaims } = useContext(AuthContext);

    const [errorMessage, setErrorMessage] = useState(null);
    const [accumulated, setAccumulated] = useState([]);
    const [nextCursor, setNextCursor] = useState('');
    const [loadingMore, setLoadingMore] = useState(false);

    // Mutation to manage data that updates
    const queryClient = useQueryClient();
    const mutation = useMutation(addNewStatement(csrfToken), {
        onSuccess: async () => {
            // Invalidate and refetch queries after adding a new statement
            queryClient.invalidateQueries(['statements', String(templateId)]);
            queryClient.invalidateQueries(['transactions', String(templateId)]);
            queryClient.invalidateQueries(['ruleHistory', String(templateId)]);
            await queryClient.refetchQueries(['transactions', String(templateId)]);
            setErrorMessage(null);
        },
        onError: (error) => {
            // Provide refined error messages based on the specific error
            let userFriendlyMessage;

            if (error.message === "no participants in template") {
                userFriendlyMessage = "You can't create a new statement for a bill with no participants. Please add participants to the bill first.";
            } else if (error.message === "insufficient permissions") {
                userFriendlyMessage = "You don't have permission to create statements for this bill.";
            } else if (error.message === "invalid template") {
                userFriendlyMessage = "This bill template is invalid or has been deleted.";
            } else if (error.message === "cost must be positive") {
                userFriendlyMessage = "Please enter a positive cost amount for the statement.";
            } else {
                // Generic error message for unknown errors
                userFriendlyMessage = `An error occurred while creating the statement: ${error.message || 'Please try again.'}`;
            }

            setErrorMessage(userFriendlyMessage);
        },
    });

    const handleAddStatement = (formData) => {
        if (formData.cost > 0) {
            formData.templateId = templateId;
            mutation.mutate(formData);
        }
    };
    const theme = useContext(ThemeContext);
    
    const isOwner = authClaims.user_sub.toUpperCase() === ownerSub.toUpperCase();

    // Query for first page (cursor ''; limit from backend). Only fetch when pane is in view (enabled).
    const { data, isLoading: isQueryLoading, isError: isQueryError } = useQuery(
        ['statements', String(templateId), { cursor: '' }],
        listTemplateStatements(csrfToken),
        {
            enabled: !!templateId && !!csrfToken && enabled !== false,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000,
        }
    );

    // Sync accumulated and next_cursor from query data; refetch resets to first page
    useEffect(() => {
        if (data) {
            setAccumulated(data.items ?? []);
            setNextCursor(data.next_cursor ?? '');
        }
    }, [data]);

    const hydratedRef = useRef(false);
    useEffect(() => {
        hydratedRef.current = false;
    }, [templateId]);
    useEffect(() => {
        // Only fire once when the query has run (enabled) and finished (success or error).
        if (onHydrated && enabled && !isQueryLoading && !hydratedRef.current && (data !== undefined || isQueryError)) {
            hydratedRef.current = true;
            onHydrated();
        }
    }, [enabled, isQueryLoading, onHydrated, data, isQueryError]);

    // Always show the button when there are items; enable when there's a next_cursor
    const showMoreButton = accumulated.length > 0;
    const canShowMore = !!nextCursor && !loadingMore;
    const handleShowMore = async () => {
        if (!canShowMore) return;
        setLoadingMore(true);
        try {
            const { items, next_cursor: next } = await fetchStatementsPage(csrfToken, templateId, nextCursor);
            setAccumulated((prev) => [...prev, ...items]);
            setNextCursor(next ?? '');
        } finally {
            setLoadingMore(false);
        }
    };

    if (!hydrating && isQueryLoading) {
        return <Typography sx={{ color: theme.colors.text }}>Loading...</Typography>;
    }

    if (isQueryError) {
        console.error('Error fetching statements:', isQueryError);
        return <Typography sx={{ color: theme.colors.text }}>Error fetching statements, try refreshing</Typography>;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {!hydrating && errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <AlertTitle>Cannot Create Statement</AlertTitle>
                    {errorMessage}
                </Alert>
            )}
            <FormStatements
                templateId={templateId}
                statements={hydrating ? [] : accumulated}
                hydrating={hydrating}
                onSubmit={handleAddStatement}
                isOwner={isOwner}
                showMoreButton={showMoreButton}
                onShowMore={handleShowMore}
                loadingMore={loadingMore}
                hasMore={!!nextCursor}
                accumulatedLength={accumulated.length}
                expandedView={expandedView}
                headerAction={headerAction}
            />
        </Box>
    );
};

export default StatementsModel;
